/**
 * PropertyCheckDashboard — the owner's list of Property Check requests.
 *
 * Mounted at /property-check/dashboard. Groups by property when the owner
 * has 2+ saved properties; falls back to a flat list otherwise.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardActionArea,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import propertyCheckService from '../services/propertyCheckService';
import ownerPropertiesService from '../services/ownerPropertiesService';
import PackageCard from '../components/property-check/PackageCard';
import { colors } from '../theme/colors';

const statusColor = (status) => {
  switch (status) {
    case 'assigned':
    case 'in_progress':
      return 'info';
    case 'verification_completed':
    case 'approved':
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

const statusLabel = (status) => {
  switch (status) {
    case 'pending':
      return 'Finding verifier';
    case 'assigned':
      return 'Verifier assigned';
    case 'in_progress':
      return 'Visit in progress';
    case 'verification_completed':
      return 'Report ready';
    case 'approved':
      return 'Approved';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return status;
  }
};

const PropertyCheckCard = ({ check, onClick }) => {
  const created = check.createdAt ? new Date(check.createdAt) : null;
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
      <CardActionArea onClick={onClick} sx={{ p: 2.5, alignItems: 'stretch', height: '100%' }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {check.package?.displayName || 'Property Check'}
            </Typography>
            <Chip size="small" label={statusLabel(check.status)} color={statusColor(check.status)} />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {check.location?.address || check.location?.formattedAddress || 'Location not specified'}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {created ? created.toLocaleDateString() : '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ·
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              ${Number(check.package?.basePriceUsd || check.payment?.amount || 0).toFixed(2)}
            </Typography>
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

const PropertyCheckDashboard = () => {
  const navigate = useNavigate();
  const [checks, setChecks] = useState([]);
  const [properties, setProperties] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, p, pkgs] = await Promise.all([
          propertyCheckService.listMine(),
          ownerPropertiesService.list(),
          propertyCheckService.listPackages().catch(() => []),
        ]);
        setChecks(c);
        setProperties(p);
        setPackages(pkgs);
      } catch (err) {
        setError(err?.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Group by property if 2+ properties exist
  const grouped = useMemo(() => {
    if (properties.length < 2) return null;
    const groups = new Map();
    for (const p of properties) groups.set(p.id, { property: p, checks: [] });
    const ungrouped = [];
    for (const c of checks) {
      if (c.ownerPropertyId && groups.has(c.ownerPropertyId)) {
        groups.get(c.ownerPropertyId).checks.push(c);
      } else {
        ungrouped.push(c);
      }
    }
    return { groups: Array.from(groups.values()).filter((g) => g.checks.length > 0), ungrouped };
  }, [checks, properties]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Property Checks
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your scheduled and completed verifications
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            onClick={() => navigate('/property-check/properties')}
          >
            Saved properties
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/property-check/book')}
          >
            Book a check
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {checks.length === 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            borderColor: colors.border.light,
            bgcolor: '#fff',
          }}
        >
          <Grid container spacing={3} alignItems="center" sx={{ mb: packages.length ? 4 : 0 }}>
            <Grid item xs={12} md={7}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                <HomeWorkIcon sx={{ color: colors.brand.primary }} />
                <Typography variant="overline" sx={{ color: colors.brand.primary, fontWeight: 800, letterSpacing: '0.08em' }}>
                  PROPERTY CHECK WORKSPACE
                </Typography>
              </Stack>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                Book your first verification
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 620 }}>
                Pick a fixed-price package, choose or save a property, and Eqaya will assign a
                verifier to collect the photos, GPS proof, checklist, and report details for that package.
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent={{ md: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/property-check/properties')}
                  startIcon={<LocationOnIcon />}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  {properties.length ? 'Manage properties' : 'Add a property'}
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/property-check/book')}
                  startIcon={<AddIcon />}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Start booking
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {packages.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Choose a package to start
              </Typography>
              <Grid container spacing={2.5}>
                {packages.map((pkg) => (
                  <Grid item xs={12} sm={6} md={4} key={pkg.slug}>
                    <PackageCard
                      pkg={pkg}
                      onSelect={() => navigate(`/property-check/book?package=${pkg.slug}`)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Paper>
      )}

      {!grouped && checks.length > 0 && (
        <Grid container spacing={2}>
          {checks.map((c) => (
            <Grid item xs={12} sm={6} md={4} key={c.id}>
              <PropertyCheckCard
                check={c}
                onClick={() => navigate(`/property-check/c/${c.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {grouped && (
        <Stack spacing={5}>
          {grouped.groups.map(({ property, checks: list }) => (
            <Box key={property.id}>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {property.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {property.location?.address || property.location?.city || ''}
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                {list.map((c) => (
                  <Grid item xs={12} sm={6} md={4} key={c.id}>
                    <PropertyCheckCard
                      check={c}
                      onClick={() => navigate(`/property-check/c/${c.id}`)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
          {grouped.ungrouped.length > 0 && (
            <Box>
              <Typography variant="overline" color="text.secondary">
                Other
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {grouped.ungrouped.map((c) => (
                  <Grid item xs={12} sm={6} md={4} key={c.id}>
                    <PropertyCheckCard
                      check={c}
                      onClick={() => navigate(`/property-check/c/${c.id}`)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Stack>
      )}
    </Container>
  );
};

export default PropertyCheckDashboard;
